import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Header } from "./components/Header";
import { HeroSection } from "./components/HeroSection";
import { ServiceHighlights } from "./components/ServiceHighlights";
import { PhotoGallerySection } from "./components/PhotoGallerySection";
import { PhotoCtaBanner } from "./components/PhotoCtaBanner";
import { ServicesSection } from "./components/ServicesSection";
import { AboutSection } from "./components/AboutSection";
import { TestimonialsSection } from "./components/TestimonialsSection";
import { BookingSection } from "./components/BookingSection";
import { ContactSection } from "./components/ContactSection";
import { Footer } from "./components/Footer";
import { Chatbot } from "./components/Chatbot";
import { FAQSection } from "./components/FAQSection";
import { ReviewHighlightsSection } from "./components/ReviewHighlightsSection";
import { TRENDING_KEYWORD_STRING } from "./components/SeoContentSections";
import { Toaster } from "./components/ui/sonner";
import { AdminLogin } from "./components/admin/AdminLogin";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import { SEO } from "./components/SEO";
import { 
  ResidentialMovingPage, 
  CommercialMovingPage, 
  MovingSupportPage,
  ResidentialCleaningPage,
  CommercialCleaningPage 
} from "./components/services";
import {
  ServicesPage,
  FAQPage,
  ReviewsPage,
  AboutPage,
  ContactPage,
  PricingPage,
  CareersPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  CertificateOfInsurancePage,
  BlogPage,
  BlogPostPage,
  LocationPage,
  ServiceAreasPage
} from "./components/pages";
import { CookieConsent } from "./components/CookieConsent";
import { resolveLocationSlug, getLocationSeoData } from "./components/pages/locationSeoData";

// ScrollToTop component to scroll to top on route change
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  
  return null;
};

const Home = () => {
  useEffect(() => {
    // Handle hash scroll on page load
    if (window.location.hash === '#booking') {
      setTimeout(() => {
        const bookingSection = document.getElementById('booking');
        if (bookingSection) {
          bookingSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Professional Movers & Cleaners in Columbus, Indianapolis & Indiana"
        description="Swift Move and Clean offers professional moving and cleaning services in Columbus, Indianapolis, Carmel, Fishers, and across Indiana. Licensed & insured movers. Free quotes. Same-day service available. Call (812) 669-4165."
        keywords={`movers Columbus IN, moving company Indianapolis, movers near me, cleaners Columbus Indiana, move-out cleaning, residential movers Carmel, commercial moving Fishers, packing services Indiana, ${TRENDING_KEYWORD_STRING}`}
        canonical="/"
      />
      <Header />
      <main>
        <HeroSection />
        <ServiceHighlights />
        <AboutSection />
        <PhotoGallerySection />
        <TestimonialsSection />
        <ReviewHighlightsSection
          title="Recent Review Highlights"
          subtitle="A few of the newest comments from customers who booked moving and packing help with Swift Move and Clean."
          className="py-16 bg-gradient-to-br from-gray-50 to-blue-50"
        />
        <BookingSection />
        <FAQSection />
        <ContactSection />
        <PhotoCtaBanner />
      </main>
      <Footer />
      <Chatbot />
      <CookieConsent />
      <Toaster />
    </div>
  );
};

// Admin Page Component with Auth State Management
const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    // Validate token on mount
    const validateToken = async () => {
      const storedToken = localStorage.getItem('admin_token');
      if (storedToken) {
        try {
          // Test if token is valid by making a request to the stats endpoint
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/stats`, {
            headers: {
              'Authorization': `Bearer ${storedToken}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            setToken(storedToken);
          } else {
            // Token is invalid, clear it
            localStorage.removeItem('admin_token');
            setToken(null);
          }
        } catch (error) {
          console.error('Token validation error:', error);
          localStorage.removeItem('admin_token');
          setToken(null);
        }
      }
      setIsValidating(false);
    };

    validateToken();
  }, []);

  const handleLoginSuccess = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-light-gray flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Validating session...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
};

// Page Wrapper with Header and Footer
const PageWrapper = ({ children }) => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>{children}</main>
      <Footer />
      <Chatbot />
      <CookieConsent />
    </div>
  );
};

const LegacyBlogPostRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
};

const RootSlugPage = () => {
  const { slug } = useParams();
  const resolvedLocationSlug = resolveLocationSlug(slug);
  const isLocationLandingPage = Boolean(getLocationSeoData(resolvedLocationSlug));

  return isLocationLandingPage ? <LocationPage /> : <BlogPostPage />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Main Navigation Pages */}
          <Route path="/services" element={<PageWrapper><ServicesPage /></PageWrapper>} />
          <Route path="/blog" element={<PageWrapper><BlogPage /></PageWrapper>} />
          <Route path="/blog/:slug" element={<LegacyBlogPostRedirect />} />
          <Route path="/service-areas" element={<PageWrapper><ServiceAreasPage /></PageWrapper>} />
          <Route path="/locations/:city" element={<PageWrapper><LocationPage /></PageWrapper>} />
          <Route path="/pricing" element={<PageWrapper><PricingPage /></PageWrapper>} />
          <Route path="/careers" element={<PageWrapper><CareersPage /></PageWrapper>} />
          <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
          <Route path="/faq" element={<PageWrapper><FAQPage /></PageWrapper>} />
          <Route path="/reviews" element={<PageWrapper><ReviewsPage /></PageWrapper>} />
          <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
          <Route path="/:slug" element={<PageWrapper><RootSlugPage /></PageWrapper>} />
          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PageWrapper><PrivacyPolicyPage /></PageWrapper>} />
          <Route path="/terms-of-service" element={<PageWrapper><TermsOfServicePage /></PageWrapper>} />
          <Route path="/certificate-of-insurance" element={<PageWrapper><CertificateOfInsurancePage /></PageWrapper>} />
          {/* Service Detail Pages */}
          <Route path="/services/residential-moving" element={<PageWrapper><ResidentialMovingPage /></PageWrapper>} />
          <Route path="/services/commercial-moving" element={<PageWrapper><CommercialMovingPage /></PageWrapper>} />
          <Route path="/services/moving-support" element={<PageWrapper><MovingSupportPage /></PageWrapper>} />
          <Route path="/services/residential-cleaning" element={<PageWrapper><ResidentialCleaningPage /></PageWrapper>} />
          <Route path="/services/commercial-cleaning" element={<PageWrapper><CommercialCleaningPage /></PageWrapper>} />
          {/* Admin Routes */}
          <Route path="/smc-admin6889" element={<AdminPage />} />
          <Route path="/smc-admin6889/login" element={<Navigate to="/smc-admin6889" replace />} />
          <Route path="/smc-admin6889/dashboard" element={<Navigate to="/smc-admin6889" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
